import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
  Box,
  BreadcrumbItem,
  Divider,
  Flex,
  Text,
  useMediaQuery
} from '@viveaesthetics/shared'
import {
  getCurrentDateInTimeZone,
  TypeOfCareEnum,
  useFeatureFlags
} from '@viveaesthetics/elemy-shared-core'
import { useAtomValue } from 'jotai'
import {
  formatSlugSegment,
  useCustomerIOAnalytics,
  useSegmentAnalytics
} from '@viveaesthetics/shared-domain'
import {
  CITIES_BY_STATE_MAP,
  STATES_MAP
} from '@viveaesthetics/elemy-shared-core/lib/constants/data'
import Head from 'next/head'

import { Subheader } from '@/components/layout/Subheader'
import {
  getFilteredProviders,
  getUrlWithPersistedParams,
  orderProvidersByNearestAvailability,
  THERAPISTS_PAGE_PERSISTED_PARAMS
} from '@/utils/providers'
import { useUserData } from '@/hooks/useUserData'
import { Container } from '@/components/layout/styles'
import { AnalyticsEvents } from '@/types/analyticsEvents'
import { Filter } from '@/components/ProvidersList/Filter/Filter'
import { ProviderInfoBlock } from '@/components/ProvidersList/ProviderInfoBlock/ProviderInfoBlock'
import { useControlSlugParams } from '@/hooks/useControlSlugParams'
import {
  NavigationBlock,
  NavigationLink
} from '@/components/ProvidersList/NavigationBlock/NavigationBlock'
import { ProviderListContent } from '@/components/ProvidersList/ProviderListContent/ProviderListContent'
import { ROUTES } from '@/constants/routes'
import { useIsInsuranceIdEnabled } from '@/hooks/useIsInsuranceIdEnabled'
import { useSelectedPaymentInfo } from '@/hooks/insurance/useSelectedPaymentInfo'
import { useProviderFilters } from '@/hooks/provider/useProviderFilters'
import { useProviderData } from '@/hooks/provider/useProviderData'
import { useProviderLocationInfo } from '@/hooks/provider/useProviderLocationInfo'
import {
  DefaultLayout,
  scrollableLayoutContainerAtom
} from '@/components/layout/DefaultLayout'

const Therapists = () => {
  const router = useRouter()

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
  const today = getCurrentDateInTimeZone(timeZone)
  const { isMobile } = useMediaQuery()
  const [pageIndex, setPageIndex] = useState<number>(0)
  const customerIOAnalytics = useCustomerIOAnalytics<AnalyticsEvents>()
  const segmentAnalytics = useSegmentAnalytics<AnalyticsEvents>()
  const [userData] = useUserData()
  const flags = useFeatureFlags()
  const prevStateRef = useRef<string | undefined>()
  const isSegmentClientDataEnabled = flags['pfp-3891-segment-client-data']
  // NOTE: on purpose did inversed logic since we need to render the SEO cities links
  //       as soon as possible for SEO purposes
  const isCitiesSeoEnabled = !!flags['pfp-4142-tsn-cities-seo']
  const isInsuranceImprovementsEnabled =
    !!flags['pedp-3730-booking-optimize-Insurance-verification']

  const scrollableContainer = useAtomValue(scrollableLayoutContainerAtom)
  const { params: slugParams } = useControlSlugParams()
  const {
    activeFilters,
    activeFiltersWithDefaultFallback,
    onChangeFilter,
    onResetFilter
  } = useProviderFilters({
    setPageIndex,
    scrollableContainer,
    useSlugParams: true
  })

  const isInsuranceIdFeatureEnabled = useIsInsuranceIdEnabled()
  const { selectedInsuranceInfo, selectedPaymentName } =
    useSelectedPaymentInfo()

  const {
    isLoading,
    providersToRender,
    slotsForProviders,
    isProviderSlotsLoading
  } = useProviderData({ activeFiltersWithDefaultFallback })

  const isProviders =
    router.query.typeOfCare === TypeOfCareEnum.MEDICATION_MANAGEMENT

  useEffect(() => {
    if (slugParams.id) {
      // NOTE: backward compatibility for old URLs /therapists/[uuid]
      router.replace(`/therapists/profile/${slugParams.id}`, undefined, {
        shallow: true
      })
      return
    }
    if (
      router.isReady &&
      !!slugParams.originalSlug?.length &&
      !slugParams.state
    ) {
      // NOTE: if state is unknown, redirect to the main page
      router.push('/therapists', undefined, { shallow: true })
    }
  }, [router, slugParams.id, slugParams.state])

  useEffect(() => {
    if (!isInsuranceImprovementsEnabled) {
      return
    }

    if (!prevStateRef.current && activeFilters.state) {
      prevStateRef.current = activeFilters.state
      return
    }

    if (activeFilters.state && activeFilters.state !== prevStateRef.current) {
      onChangeFilter('insurance', undefined)
      prevStateRef.current = activeFilters.state
    }
  }, [activeFilters.state, isInsuranceImprovementsEnabled])

  useEffect(() => {
    if (
      activeFilters.state ||
      activeFilters.insurance ||
      activeFilters.concern
    ) {
      customerIOAnalytics.identify(userData.email || null, {
        state: activeFilters.state,
        insurance: activeFilters.insurance,
        concern: activeFilters.concern
      })
      if (isSegmentClientDataEnabled) {
        segmentAnalytics.identify(userData.email || null, {
          state: activeFilters.state,
          insurance: activeFilters.insurance,
          concern: activeFilters.concern
        })
      }
    }
  }, [activeFilters.state, activeFilters.insurance, activeFilters.concern])

  useEffect(() => {
    if (isInsuranceIdFeatureEnabled) {
      const data = {
        ...activeFilters,
        ...(selectedPaymentName ? { insurance: selectedPaymentName } : {}),
        ...(selectedInsuranceInfo?.id
          ? { insuranceId: selectedInsuranceInfo.id }
          : {})
      }
      customerIOAnalytics.trackEvent(
        AnalyticsEvents.BOOKING_FILTER_CHANGE,
        data
      )
      if (isSegmentClientDataEnabled) {
        segmentAnalytics.trackEvent(AnalyticsEvents.BOOKING_FILTER_CHANGE, data)
      }
    } else {
      customerIOAnalytics.trackEvent(
        AnalyticsEvents.BOOKING_FILTER_CHANGE,
        activeFilters
      )
      if (isSegmentClientDataEnabled) {
        segmentAnalytics.trackEvent(
          AnalyticsEvents.BOOKING_FILTER_CHANGE,
          activeFilters
        )
      }
    }
  }, [activeFilters])

  const filteredProviders = getFilteredProviders({
    providers: orderProvidersByNearestAvailability(
      providersToRender,
      slotsForProviders,
      today
    ),
    activeFilters,
    slotsForProviders,
    isInsuranceImprovementsEnabled
  })

  const { location, title, description, cityName } =
    useProviderLocationInfo(activeFilters)

  const { state = '' } = activeFiltersWithDefaultFallback
  const stateSlug = formatSlugSegment(STATES_MAP[state] || '')
  const citiesLinks: NavigationLink[] = Object.values(
    CITIES_BY_STATE_MAP[state] || {}
  ).map(cityName => ({
    label: cityName,
    url: `${ROUTES.THERAPISTS}/${stateSlug}/${formatSlugSegment(cityName)}`
  }))

  const breadcrumbLinks: BreadcrumbItem[] = [
    {
      label: 'States',
      path: getUrlWithPersistedParams(
        ROUTES.THERAPISTS,
        THERAPISTS_PAGE_PERSISTED_PARAMS
      )
    },
    {
      label: STATES_MAP[state],
      path: getUrlWithPersistedParams(
        `${ROUTES.THERAPISTS}/${stateSlug}`,
        THERAPISTS_PAGE_PERSISTED_PARAMS
      )
    }
  ]
  if (cityName) {
    breadcrumbLinks.push({
      label: cityName,
      path: `${ROUTES.THERAPISTS}/${stateSlug}/${formatSlugSegment(cityName)}`
    })
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta
          name="description"
          content={description}
        />
      </Head>
      <Flex
        align="center"
        justify="center"
        fullHeight
        template={[1]}
      >
        <Box
          $padding={[
            isMobile ? 'spacing24' : 'spacing40',
            'spacing0',
            isMobile ? 'spacing40' : 'spacing64',
            'spacing0'
          ]}
        >
          <Subheader>
            <Filter
              activeFilters={activeFilters}
              onChange={onChangeFilter}
            />
          </Subheader>
          <ProviderListContent
            isLoadingProviderData={isLoading}
            isProviderSlotsLoading={isProviderSlotsLoading}
            activeFiltersWithDefaultFallback={activeFiltersWithDefaultFallback}
            filteredProviders={filteredProviders}
            onChangeFilter={onChangeFilter}
            onResetFilter={onResetFilter}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            slotsForProviders={slotsForProviders}
            listContentHeader={
              <>
                {isCitiesSeoEnabled ? (
                  <Container size="widthM">
                    <Text
                      as="h1"
                      type="title1"
                    >
                      {`${STATES_MAP[state]} ${
                        isProviders ? 'Providers' : 'Therapists'
                      }`}
                    </Text>
                    <NavigationBlock
                      breadcrumbLinks={breadcrumbLinks}
                      providerCount={filteredProviders.length}
                      briefLinksCount={12}
                      location={location}
                      showListButton={citiesLinks.length > 0}
                      showListLabel={
                        CITIES_BY_STATE_MAP[state] && slugParams.city
                          ? CITIES_BY_STATE_MAP[state]?.[slugParams.city] ||
                            'All cities'
                          : 'All cities'
                      }
                      linksList={citiesLinks}
                    />
                  </Container>
                ) : (
                  <>
                    <Container size="widthM">
                      <ProviderInfoBlock
                        location={location}
                        providerCount={filteredProviders.length}
                        insurance={selectedPaymentName}
                      />
                    </Container>
                    <Divider />
                  </>
                )}
              </>
            }
          />
        </Box>
      </Flex>
    </>
  )
}

export default Therapists

Therapists.getLayout = (page: React.ReactElement) => (
  <DefaultLayout hasSubheader>{page}</DefaultLayout>
)
